export const SubscriptionProvider = {
  MANUAL: 'manual',
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
} as const;
export type SubscriptionProvider =
  (typeof SubscriptionProvider)[keyof typeof SubscriptionProvider];

export const SubscriptionStatus = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
  /** `suspended` — set by the lifecycle cron after 14 days past_due.
   *  Login is restricted to /account/billing only until payment lands. */
  SUSPENDED: 'suspended',
} as const;
export type SubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const SubscriptionPaymentStatus = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  VOID: 'void',
} as const;
export type SubscriptionPaymentStatus =
  (typeof SubscriptionPaymentStatus)[keyof typeof SubscriptionPaymentStatus];

export const SubscriptionPaymentMethod = {
  TRANSFER: 'transfer',
  CARD: 'card',
  CASH: 'cash',
  CHECK: 'check',
  PAYPAL: 'paypal',
  CUSTOM: 'custom',
} as const;
export type SubscriptionPaymentMethod =
  (typeof SubscriptionPaymentMethod)[keyof typeof SubscriptionPaymentMethod];

export interface BusinessSubscription {
  id: string;
  businessId: string;
  plan: string;
  subscriptionStatus: SubscriptionStatus;
  provider: SubscriptionProvider;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  pendingPlan: string | null;
  currency: string;
  defaultPaymentMethod: SubscriptionPaymentMethod | null;
  customDefaultPaymentMethodName: string | null;
  providerCustomerId?: string | null;
  providerSubscriptionId?: string | null;
  providerDefaultPaymentMethodId?: string | null;
  /** PayPal Vault payment token + cached card metadata. */
  providerPaymentTokenId?: string | null;
  providerPaymentTokenBrand?: string | null;
  providerPaymentTokenLast4?: string | null;
  preferredPaymentProvider?: 'stripe' | 'paypal' | null;
  lastPlanChangeAt?: string | null;
  isSponsored?: boolean;
  billingInterval?: 'monthly' | 'annual' | null;
  /**
   * The catalog template code the business is currently on (e.g.
   * `basico`, `empresarial`). Stamped by
   * `SubscriptionItemSeedService.applyCatalogPlanToBusiness` after
   * registration. Surfaces here so admin-facing modals can render
   * the SAME plan label the customer sees on /account (resolved via
   * /public/plans) instead of the legacy `plan` enum.
   */
  catalogPlanTemplateCode?: string | null;
  catalogPlanTemplateId?: string | null;
}

export interface SubscriptionPayment {
  id: string;
  businessId: string;
  subscriptionId: string;
  paymentStatus: SubscriptionPaymentStatus;
  provider?: SubscriptionProvider;
  amount: string;
  currency: string;
  paymentMethod: SubscriptionPaymentMethod;
  customPaymentMethodName: string | null;
  referenceNumber: string | null;
  paymentDate: string;
  periodStart: string | null;
  periodEnd: string | null;
  notes: string | null;
  providerPaymentId?: string | null;
  /** Internal monthly invoice number (`INV-YYYYMM-NNNNNN`). */
  invoiceNumber?: string | null;
}

export interface StripeSetupIntentResponseDto {
  clientSecret: string;
}

export interface StripeDefaultPaymentMethodSummaryDto {
  paymentMethodId: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

export interface StripeSetDefaultPaymentMethodDto {
  paymentMethodId: string;
}

export interface StripePayDto {
  amount: number;
  currency?: string;
  description?: string;
  paymentDate?: string;
}

export interface PaypalCreateOrderDto {
  amount: number;
  currency?: string;
  description?: string;
  returnUrl?: string;
  cancelUrl?: string;
}

export interface PaypalCreateOrderResponseDto {
  orderId: string;
  approveUrl: string;
}

export interface PaypalCaptureOrderDto {
  orderId: string;
}

export interface SubscriptionPaymentsParams {
  page?: number;
  limit?: number;
}

export interface UpdateBusinessSubscriptionDto {
  plan?: string;
  subscriptionStatus?: SubscriptionStatus;
  isSponsored?: boolean;
}

export interface UpdateMySubscriptionDto {
  plan?: string;
  preferredPaymentProvider?: 'stripe' | 'paypal';
  billingInterval?: 'monthly' | 'annual';
}

export interface UpdateMySubscriptionResponse extends BusinessSubscription {
  isDowngrade?: boolean;
  paypalApproveUrl?: string;
}

export interface TrialStatus {
  isTrialActive: boolean;
  isTrialExpired: boolean;
  daysRemaining: number;
  trialStartDate: string;
  trialEndDate: string;
  plan: string;
}

/**
 * Verdict returned by GET /subscription/me/access.
 * Authoritative server-side gate for whether a business may operate
 * the app.
 */
export type SubscriptionAccessStatus =
  | 'allowed'
  | 'trial_only'
  | 'no_subscription'
  | 'expired';

export interface SubscriptionAccessToken {
  /** JWT signed by the API with { businessId, status, expiresAt }. Cached by the client. */
  jwt: string;
  /** ISO timestamp at which the JWT expires. */
  expiresAt: string;
}

export interface BusinessSubscriptionAccess {
  status: SubscriptionAccessStatus;
  plan: string;
  subscriptionStatus: SubscriptionStatus;
  isTrialActive: boolean;
  trialEndDate: string | null;
  currentPeriodEnd: string | null;
  /** True when status === 'allowed' due to past_due grace period (≤7 days since paymentFailedAt). */
  inPastDueGrace: boolean;
  /** ISO timestamp the verdict was evaluated on the server. */
  evaluatedAt: string;
  /** Cached access token the client replays at boot. */
  accessToken: SubscriptionAccessToken;
}

export interface CreateSubscriptionPaymentDto {
  amount: number;
  paymentMethod: SubscriptionPaymentMethod;
  paymentStatus?: SubscriptionPaymentStatus;
  currency?: string;
  customPaymentMethodName?: string;
  referenceNumber?: string;
  paymentDate?: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string;
  providerPaymentId?: string;
}

export interface PlanLimits {
  /**
   * e-CF cap per calendar month (soft-cap → overage accrual).
   * Bumped by `ecf_invoices` addon slots.
   */
  maxInvoicesPerMonth: number;
  /**
   * Total-invoices cap per calendar month (B-series + non-fiscal
   * Pre Factura + e-CF combined). Hard-block. Sourced from the
   * `invoices` catalog quota. -1 = unlimited.
   */
  maxInvoicesTotal: number;
  maxProducts: number;
  maxUsers: number;
  maxWarehouses: number;
  maxBranches: number;
  maxKdsScreens: number;
  posEnabled: boolean;
  kdsEnabled: boolean;
}

export interface OveragePricing {
  extraBranchPrice: number | null;
  extraUserPrice: number | null;
  apiAccessMonthlyPrice: number | null;
  kdsAddonMonthlyPrice: number | null;
  currency: string;
}

/**
 * Capacity / quota add-on codes a business can buy. Stored on billing
 * charge lines as `addon:<type>` (e.g. `addon:extra_user × 3`). These
 * are enum codes, NOT rows in `catalog_addon`, so any surface that
 * renders a charge line must map them to a display label through
 * {@link SUBSCRIPTION_ADDON_TYPE_LABELS} — the single source of truth
 * shared by the web client, the mobile client, and the server-side
 * comprobante line writer.
 */
export type SubscriptionAddonType =
  | 'extra_branch'
  | 'extra_user'
  | 'extra_warehouse'
  | 'extra_product'
  | 'extra_kds_screen'
  | 'extra_ecf'
  | 'api_access'
  | 'kds_addon';

/**
 * Spanish display label for each {@link SubscriptionAddonType}. The ONE
 * place these capacity add-on codes get a human name — FE catalog-labels
 * and the backend `resolveLineDescriptions` both read from here so the
 * comprobante line matches what the tenant sees on /account.
 */
export const SUBSCRIPTION_ADDON_TYPE_LABELS: Record<
  SubscriptionAddonType,
  string
> = {
  extra_user: 'Usuarios extra',
  extra_branch: 'Sucursales extra',
  extra_warehouse: 'Almacenes extra',
  extra_product: 'Productos extra',
  extra_kds_screen: 'Pantallas KDS extra',
  extra_ecf: 'e-CF extra',
  api_access: 'Acceso API',
  kds_addon: 'Pantallas KDS extra',
};

export interface SubscriptionAddon {
  id: string;
  businessId: string;
  addonType: SubscriptionAddonType;
  quantity: number;
  pricePerUnit: string;
  currency: string;
  active: boolean;
}

export interface AddonSlotUsage {
  purchased: number;
  inUse: number;
  unused: number;
  nearestExpiry: string | null;
}

export interface AddonInfo {
  plan: string;
  extraBranchSlots: number;
  extraUserSlots: number;
  apiAccessActive: boolean;
  kdsAddonActive: boolean;
  overagePricing: OveragePricing | null;
  addons: SubscriptionAddon[];
  /**
   * Prepaid e-CF packs — a balance that DEPLETES and carries across monthly
   * resets, deliberately separate from the monthly figures above (which
   * reset each period). Never merge the two in the UI: a combined gauge
   * makes the prepaid portion look like it refills every month.
   */
  prepaid: PrepaidCapacity;
  slotUsage: {
    branches: AddonSlotUsage;
    users: AddonSlotUsage;
    /**
     * Added 2026-10 for parity — the reduce affordance needs to know
     * whether a warehouse / KDS slot is free, same as branches and users.
     */
    warehouses: AddonSlotUsage;
    kdsScreens: AddonSlotUsage;
  };
}

export interface PrepaidPackPreview {
  /** Per-slot price for THIS quantity's volume tier. Null = no tier configured. */
  unitPrice: number | null;
  totalAmount: number;
  currency: string;
  /** 12 months out, as the purchase would persist it. */
  expiresAt: string;
}

export interface PrepaidPackPurchaseResponse {
  addon: SubscriptionAddon;
  /**
   * True when this request matched an existing purchase by idempotency key
   * — nothing was charged. The UI shows a neutral "already processed"
   * message rather than a second success toast.
   */
  alreadyProcessed: boolean;
  charged: number;
  currency: string;
}

/** One purchased prepaid pack and how much of it is left. */
export interface PrepaidPack {
  id: string;
  /** Slots bought. Fixed at purchase. */
  purchased: number;
  /** Slots already drawn. */
  consumed: number;
  /** `purchased - consumed`. The number the operator actually cares about. */
  remaining: number;
  /** Forfeited at this instant, without refund. Null = never expires. */
  expiresAt: string | null;
}

export interface PrepaidCapacity {
  /** Sum of `remaining` across live packs. */
  totalRemaining: number;
  /** Ordered by expiry — the same order the backend draws them down. */
  packs: PrepaidPack[];
}

export interface PurchaseAddonDto {
  addonType: SubscriptionAddonType;
  quantity: number;
  returnUrl?: string;
  cancelUrl?: string;
}

export type PurchaseAddonResponse =
  | {
      provider: 'stripe' | 'manual';
      addon: SubscriptionAddon;
      message: string;
      charged: number;
      currency: string;
    }
  | {
      provider: 'paypal';
      requiresPaypalApproval: true;
      paypalApproveUrl: string;
      orderId: string;
      charged: number;
      currency: string;
    };

/**
 * Read-only estimate for an addon PURCHASE (capacity increase). Mirrors
 * the `reduce/preview` contract — never mutates. The FE confirm modal
 * renders the ACTUAL charge the operator will see, which on an ANNUAL
 * sub is the monthly rate prorated across the months remaining in the
 * cycle (`monthlyRate × qty × monthsRemaining`), billed on the next
 * (annual) invoice — NOT a one-month figure. `proratedTotal` is the
 * single number the operator pays for this selection; `chargeOnDate` is
 * when it lands (the current period end / next renewal, or null for a
 * manual/trial sub with no scheduled cycle).
 */
export interface PurchaseAddonPreviewResponse {
  addonType: string;
  quantity: number;
  /** Full monthly rate per unit (the stored recurring rate). */
  unitPriceMonthly: number;
  /** `unitPriceMonthly × quantity` — one full month for the whole selection. */
  fullMonthly: number;
  billingInterval: 'monthly' | 'annual';
  /** Proration factor: ≤1 on a monthly sub, up to 12 on an annual sub. */
  monthsRemaining: number;
  /** The actual amount charged for this selection (fullMonthly × monthsRemaining). */
  proratedTotal: number;
  currency: string;
  /** ISO date the charge lands (current period end / next invoice), or null. */
  chargeOnDate: string | null;
  /**
   * The full volume-discount tier table for this addon (sorted by
   * minQuantity), so the modal can show all levels + highlight the one
   * the current quantity lands in. Empty when the addon has no catalog
   * tiers (legacy flat pricing).
   */
  tiers: Array<{
    minQuantity: number;
    maxQuantity: number;
    perUnitMonthly: number;
  }>;
}

export interface PlanUsage {
  plan: string;
  limits: PlanLimits;
  usage: {
    /**
     * ECF count for the current calendar month. Narrowed to `ncf
     * LIKE 'E%'` so it matches the semantics of
     * `maxInvoicesPerMonth` (fed by the `ecf_invoices` quota).
     */
    invoicesThisMonth: number;
    /**
     * All non-draft invoices this month (any NCF prefix + non-fiscal
     * Pre Factura). Feeds the "Facturas / mes" gauge on /account —
     * distinct from `invoicesThisMonth`, which stays narrowed to
     * e-CF for overage-accrual math.
     */
    allInvoicesThisMonth: number;
    products: number;
    users: number;
    warehouses: number;
    branches: number;
    kdsScreens: number;
  };
  /**
   * Per-quota DOP overage rate (soft-cap). `null` = hard-block.
   * Today only `ecfInvoices` is wired; future quotas follow.
   */
  overageRates: {
    ecfInvoices: number | null;
  };
  /**
   * Current-month accrued overage, ready to render on /account.
   */
  overageAccrual: {
    ecfInvoices: {
      units: number;
      ratePerUnitDop: number;
      amountDop: number;
    } | null;
  };
  overagePricing: OveragePricing | null;
  /**
   * Active sadmin courtesy grants. Surfaced on /account so the
   * operator sees the offer (e.g. "50 e-CF extra a RD$1 — Para
   * siempre"). Empty array when no active overrides exist.
   */
  courtesyGrants: CourtesyGrant[];
}

export interface CourtesyGrant {
  /** Quota code (`users`, `ecf_invoices`, etc.). */
  quotaCode: string;
  quantity: number;
  /** Sadmin-recorded comp value in DOP (audit / display only). */
  compValueDop: number | null;
  /** Per-unit DOP rate when set — otherwise the grant is free. */
  specialPricePerUnitDop: number | null;
  /** ISO string. Null when isPerpetual is true. */
  expiresAt: string | null;
  isPerpetual: boolean;
  reason: string | null;
}

/**
 * Returned on the invoice create response when an e-CF emit
 * landed past the plan's included cap and accrued an overage unit.
 * The FE pops a toast and the /account capacity badge refetches.
 */
export interface InvoiceOverageApplied {
  quotaCode: 'ecf_invoices';
  units: number;
  ratePerUnitDop: number;
  amountDop: number;
}

export interface PaymentFailureStatus {
  hasFailure: boolean;
  isDeactivated: boolean;
  failureReason: string | null;
  failedAt: string | null;
  retryCount: number;
}

export interface PaypalSubscriptionInput {
  returnUrl: string;
  cancelUrl: string;
}

export interface PaypalSubscriptionResponse {
  approveUrl: string;
  paypalSubscriptionId: string;
}

export interface RetryPaymentResponse {
  success: boolean;
  message: string;
}
